
import {
  computePosition,
  autoPlacement,
  offset,
  shift,
  flip
} from '@floating-ui/dom';

import { nanoid } from 'nanoid';
import clamp from '../helpers/Clamp';

export default {
  props: {
    show: {
      type: Boolean,
      default: undefined
    },

    align: {
      type: String,
      default: 'auto'
    },

    label: {
      type: String,
      default: ''
    },

    anchor: {
      type: null,
      required: false,
      default: undefined
    },

    delay: {
      type: [Number, String],
      default: 1000
    },

    offset: {
      type: [Number, String],
      default: 4
    },

    type: {
      type: String,
      validator: val => ['tooltip', 'info'].includes(val),
      default: 'info'
    }
  },

  data () {
    return {
      id: nanoid(),
      top: 0,
      left: 0,
      anchorElement: undefined,
      internalShow: this.show,
      timeout: undefined
    };
  },

  computed: {
    style () {
      return {
        top: this.top + 'px',
        left: this.left + 'px'
      };
    },

    alignments () {
      return this.align.split('-');
    }
  },

  watch: {
    show () {
      this.internalShow = this.show;
    },

    align () {
      this.alignBubble();
    },

    async internalShow () {
      this.$emit('update:show', this.internalShow);
      await new Promise(resolve => setTimeout(resolve, 125));
      this.alignBubble();
    },

    anchor () {
      if (this.anchorEelement) {
        this.anchorElement.removeAttribute('aria-describedby');
      }

      this.anchorElement = this.getAnchor();

      if (this.anchorElement) {
        this.anchorElement.setAttribute('aria-describedby', this.id);
      }

      this.alignBubble();
    }
  },
  mounted () {
    this.createEvents();
    this.anchorElement = this.getAnchor();

    if (this.anchorElement) {
      this.anchorElement.setAttribute('aria-describedby', this.id);
    }
  },

  methods: {
    open (x, y) {
      this.internalShow = true;
      this.alignBubble(x, y);
    },

    createEvents () {
      if (!process.browser) { return; }

      window.addEventListener('scroll', () => this.forceHide(), {
        passive: true
      });

      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.internalShow) {
          this.internalShow = false;
        }
      });

      this.anchorElement = this.getAnchor();

      if (this.show !== undefined) { return; }

      const hoverElement = this.$el.parentElement;

      // eslint-disable-next-line no-console
      if (!hoverElement) { console.warn('No hover element found.'); }

      if (hoverElement) {
        hoverElement.addEventListener('mouseenter', () =>
          this.startDelay(this.delay)
        );

        hoverElement.addEventListener('mouseleave', () => {
          this.internalShow = false;
          if (this.timeout) { clearTimeout(this.timeout); }
        });

        hoverElement.addEventListener('touchstart', (e) => {
          e.preventDefault();
          this.startDelay(100);
        });
        hoverElement.addEventListener('touchend', (e) => {
          e.preventDefault();
          this.internalShow = false;
        });
      }
    },

    startDelay (delay) {
      if (this.show !== undefined) { return; }

      if (this.timeout) { clearTimeout(this.timeout); }

      this.timeout = setTimeout(() => (this.internalShow = true), delay);
    },

    getAnchor () {
      if (this.anchor) {
        if (this.anchor instanceof Element) {
          return this.anchor;
        } else if (typeof this.anchor === 'string') {
          return document.querySelector(this.anchor);
        } else if (this.anchor.$el && this.anchor.$el instanceof Element) {
          return this.anchor.$el;
        }
      }

      if (this.$el.parentElement instanceof Element) { return this.$el.parentElement; }
    },

    async alignBubble (x, y) {
      if (!process.browser) { return; }

      if (this.align === 'manual') {
        this.left = clamp(x ?? this.left, 8, window.innerWidth - this.$refs.bubble.clientWidth - 8);
        this.top = clamp(y ?? this.top, 8, window.innerHeight - this.$refs.bubble.clientHeight - 8);
        return;
      }

      const middlewares = [offset(this.offset), flip(), shift({ padding: 8 })];

      if (this.align === 'auto') { middlewares.splice(1, 1, autoPlacement()); }

      const position = await computePosition(
        this.anchorElement,
        this.$refs.bubble,
        {
          middleware: middlewares,
          placement: this.align === 'auto' ? undefined : this.align,
          strategy: 'fixed'
        }
      );

      this.left = position.x;
      this.top = position.y;
    },

    async forceHide () {
      this.internalShow = false;
      this.$el.style.display = 'none';

      await this.$nextTick();

      delete this.$el.style.display;
    }
  }
};
